/**
 * printToken — 80mm thermal printer token generator
 *
 * Uses a hidden iframe to trigger window.print() without disrupting the main UI.
 * Designed for 80mm thermal receipt printers (no cutting needed).
 *
 * Usage:
 *   printToken({ receipt_number, queue_number, patient, doctor_name, payment_mode, visit_time })
 *
 * Phase 1 — procedure payments:
 *   printToken({
 *     ...existing,
 *     procedures: [{ name, amount, receipt_number }, ...],
 *     consultation_fee,                 // optional — shown alongside procedures
 *   })
 *
 * When `procedures` is passed, the token adds a "PROCEDURES" section listing
 * each paid sitting and a "TOTAL PAID TODAY" summary line. A full itemised
 * GST-aware PDF receipt is deferred to Phase 4; Phase 1 prints this minimal
 * extension so the patient walks away with their total paid.
 *
 * Issue-1 (2026-07-31) — canonical-receipts line items:
 *   printToken({
 *     ...existing,
 *     line_items: [{ name, amount, quantity? }, ...],   // from receiptsApi#tokenLineItemsFromReceipts
 *   })
 *
 * W2/C1 (2026-08-01) — a line item's optional `quantity` (only set when > 1,
 * omitted otherwise — old-backend / qty===1 rows print byte-identical to
 * Issue-1) renders as "Name × N" on the line's name span.
 *
 * `line_items` is additive and mutually exclusive with `procedures` — if a
 * caller ever passed both, `procedures` wins (the line_items section only
 * renders when `procedures` is empty). It reuses the existing `.proc-row`
 * styling and the existing "Amount Paid" label for its total (no new
 * section header, no new user-facing string — owner ruling 2026-07-31).
 * The standalone Amount Paid row is suppressed when either block renders,
 * so the total is never printed twice.
 *
 * Issue-2 (2026-08-01) — refunds + cancelled banner:
 *   printToken({
 *     ...existing,
 *     refund_lines: [{ name, amount, receipt_number, receipt_series? }, ...],
 *                    // from receiptsApi#refundLinesFromReceipts — same shape
 *                    // as `procedures`/`line_items`, reuses `.proc-row`.
 *                    // `receipt_series` (W7, 2026-08-02) — omitted/null for
 *                    // a money-series refund (renders "· Rx N", unchanged);
 *                    // "CN" for a new credit-note refund (renders "· CN N"
 *                    // — copy PROVISIONAL, PO/Business to ratify).
 *     cancelled: true | false,                                // visit.status === 'CANCELLED'
 *   })
 *
 * F2 (owner live-test finding, 2026-08-02) — department on the token:
 *   printToken({
 *     ...existing,
 *     department_name,   // the visit's department-of-care display name;
 *                         // additive, omitted → no "Department" row (same
 *                         // .row pattern as Patient/UHID/Doctor).
 *   })
 *
 * GST-DOCNO/A2-d (R-GD-10) — the "Receipt No." row prefers the server's
 * stamped document-number string over the bare int:
 *   printToken({
 *     ...existing,
 *     receipt_number,            // unchanged — the bare int, still the
 *                                 // fallback when no printed string exists.
 *     receipt_number_printed,    // NEW — string | null | undefined. The
 *                                 // server's real document number, e.g.
 *                                 // "26-27/42". Rendered VERBATIM, never
 *                                 // composed client-side (INVARIANT F).
 *                                 // null/absent (pre-change payment, or an
 *                                 // older backend) → falls back to
 *                                 // `receipt_number`, byte-identical to
 *                                 // today.
 *   })
 *
 * Both are additive and independent of each other (mockup gate 2026-08-01):
 * - `refund_lines.length > 0` → dashed "Refunds" section (mockup 1a-v2) +
 *   a stacked Amount Paid / Refunded / Net Paid total, REPLACING the
 *   standalone Amount Paid row (never printed twice).
 * - `cancelled` → plain "CANCELLED" inverse (solid #000) banner (mockup
 *   1b), keyed to visit status ONLY — never to refund state. Boxed Net Paid
 *   ₹0 renders whenever cancelled && net === 0 (with or without refunds).
 *   `cancelled && net > 0` prints the ratified footnote "No refund
 *   recorded" inside the banner. Money is always the truthful net — a
 *   cancelled-but-unrefunded visit keeps its Net > 0.
 * When neither is passed, the token renders byte-identical to Issue-1.
 *
 * BUSY-VOUCHER R1 (OR-48) — one "Advised by" row on Lab & Diagnostic tokens:
 *   printToken({
 *     ...existing,
 *     advised_by_label,   // "Dr. Anil Sharma" | "SELF" (from tokenProviderFields).
 *                          // When given, renders ONE `Advised by` row in the
 *                          // Doctor row's place and skips both the Doctor row
 *                          // and the old `Advised by: Doctor` row. Absent →
 *                          // byte-identical to today (OPD / OTC tokens).
 *   })
 */

import { resolveUhid } from './uhid';
import { formatDateIST } from './formatIST';

const PAYMENT_LABELS = { CASH: 'Cash', CARD: 'Card', UPI: 'UPI' };

function formatTime(val) {
  if (!val) return '';
  try {
    const bare = /^(\d{1,2}):(\d{2})/.exec(val);
    if (bare) {
      const h = parseInt(bare[1], 10);
      const m = bare[2];
      const period = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      return `${h12}:${m} ${period}`;
    }
    const d = new Date(val);
    if (isNaN(d)) return val;
    // toUpperCase: Node/ICU renders am/pm lowercase, unlike the manual
    // 'AM'/'PM' branch above — keep both branches' output consistent.
    return d.toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit', minute: '2-digit', hour12: true,
    }).toUpperCase();
  } catch {
    return val;
  }
}

function formatDate(val) {
  const d = val ? new Date(val) : new Date();
  const source = isNaN(d) ? new Date() : d;
  // Fixed "DD Mon YYYY" in IST — toLocaleDateString('en-IN') is runtime-CLDR dependent.
  return formatDateIST(source.toISOString());
}

function formatCurrency(n) {
  if (n === null || n === undefined || n === '') return '';
  const v = Number(n);
  if (!Number.isFinite(v)) return String(n);
  return `₹${v.toFixed(0)}`;
}

// HTML-escape any backend-sourced string before interpolating it into the
// token's HTML template. The output is written into a same-origin iframe via
// document.write(), so an unescaped `<script>` would execute in the auth'd
// origin. Mirrors the helper in VisitAssignmentsSlip.jsx.
function escapeHtml(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/\//g, '&#x2F;');
}

export function printToken({
  receipt_number,
  receipt_number_printed,   // GST-DOCNO/A2-d — see header doc.
  queue_number,
  patient,
  doctor_name,
  payment_mode,
  visit_time,
  visit_date,
  clinic_name,
  clinic_description,
  address,
  pincode,
  phone,
  procedures,
  consultation_fee,
  line_items,        // Issue-1 — [{ name, amount }] from tokenLineItemsFromReceipts;
                      // additive, mutually exclusive with `procedures` (see header doc).
  amount_paid,       // TOKEN-UPDATE T1 FE5 — server-sourced total collected on
                      // the visit (all types); drives the standalone Amount
                      // Paid row. `consultation_fee` stays procedure-token
                      // "Total paid today" block input only.
  advised_by,        // 'DOCTOR' | 'SELF' — who advised the work (Nurse/Lab visits);
                      // row prints only when 'DOCTOR' (S10) — self/walk-in omits it
  advised_by_label,  // BUSY-VOUCHER R1 (OR-48) — Lab & Diagnostic token: "Dr. X" | "SELF";
                      // when given, ONE "Advised by" row replaces the Doctor + old Advised-by rows
  assignee_label,    // 'Nurse' | 'Lab Technician' — the row label for the assignee
  assignee_name,     // the assigned nurse / lab technician name
  refund_lines,      // Issue-2 — [{ name, amount, receipt_number }] from
                      // receiptsApi#refundLinesFromReceipts; additive, see header doc.
  cancelled,         // Issue-2 — bool, visit.status === 'CANCELLED'; additive, see header doc.
  department_name,   // F2 — the visit's department-of-care display name; additive, see header doc.
}) {
  const patientName = patient
    ? `${patient.first_name || ''} ${patient.last_name || ''}`.trim()
    : '—';
  const paymentLabel = PAYMENT_LABELS[payment_mode] || payment_mode || '';
  const clinicLabel = clinic_name || 'Clinic';
  const dateLabel = formatDate(visit_date);
  // S2/AC2: never time-only, never blank — when no visit_time is available
  // (e.g. procedure-payment token), fall back to the current print time.
  const timeLabel = formatTime(visit_time) || formatTime(new Date());
  const datetimeLabel = `${dateLabel} · ${timeLabel}`;
  const descriptionHtml = clinic_description
    ? escapeHtml(clinic_description).replace(/\r?\n/g, '<br>')
    : '';
  // GST-DOCNO/R-GD-6, superseded by T-UHID-BUG (2026-08-26) — prefer the
  // real UHID (`PatientResponse.uhid` / `VisitResponse.patient_uhid` /
  // sitting-collect `patient_uhid`, threaded through as `patient.uhid` by
  // every caller); fall back to the FULL `patient_id` (never a sliced
  // fragment — this is a money document) when the caller has no real one.
  const uhid = resolveUhid({ uhid: patient?.uhid, patient_id: patient?.patient_id });

  // GST-DOCNO/A2-d (R-GD-10) — the "Receipt No." row is one number across
  // the receipt PDF, the thermal token and the on-screen line. Prefer the
  // server-stamped `receipt_number_printed` string; fall back to the bare
  // `receipt_number` int, byte-identical to today, when the field is
  // null/absent (pre-change payment or an older backend). Never composed
  // client-side — this is a pick between two already-formed server values.
  const receiptNumberDisplay = receipt_number_printed != null ? receipt_number_printed : receipt_number;

  const addressLine = [address, pincode].filter(Boolean).join(' ').trim();

  const procedureList = Array.isArray(procedures) ? procedures : [];
  const consultationAmount = Number(consultation_fee || 0);
  const proceduresTotal = procedureList.reduce(
    (sum, p) => sum + Number(p.amount || 0), 0,
  );
  const totalPaid = consultationAmount + proceduresTotal;
  // Issue-1: `procedures` and `line_items` are mutually exclusive — a
  // line_items caller never also passes procedures (see printToken() doc).
  // Guarding on `procedureList.length === 0` here means a `procedures` caller
  // can never enter the line_items branch even if both were ever passed.
  const lineItemsList = procedureList.length === 0 && Array.isArray(line_items) ? line_items : [];
  const lineItemsTotal = lineItemsList.reduce(
    (sum, li) => sum + Number(li.amount || 0), 0,
  );
  // Issue-2 — refunds + cancelled. `refund_lines` share the `procedures`/
  // `line_items` item shape ({ name, amount, receipt_number }) so the
  // dashed Refunds section reuses the same `.proc-row` markup below.
  const refundLinesList = Array.isArray(refund_lines) ? refund_lines : [];
  const hasRefunds = refundLinesList.length > 0;
  const refundedTotal = refundLinesList.reduce(
    (sum, rl) => sum + Number(rl.amount || 0), 0,
  );

  // D1 fix: the Total block belongs to the procedure/line-items token only
  // (owner layout "(procedures/total when present, unchanged)"). A plain OPD
  // token shows just the standalone Amount Paid row. When procedures OR
  // line_items ARE present, the block covers the whole payment story, so the
  // standalone Amount Paid row is suppressed to avoid printing the total twice.
  // Issue-2: when refunds are present, the stacked Amount Paid/Refunded/Net
  // Paid block below owns the total instead — suppress this block too so
  // the gross amount is never printed twice.
  const showTotalBlock = (procedureList.length > 0 || lineItemsList.length > 0) && !hasRefunds;
  const amountPaidValue = Number(amount_paid || 0);
  const isCancelled = !!cancelled;
  const netPaidValue = amountPaidValue - refundedTotal;
  // Stacked Amount Paid / Refunded / Net Paid block: renders whenever there
  // are refunds to itemise, OR the visit is cancelled with a truthful net
  // of 0 (nothing owed either way) — replaces the standalone Amount Paid
  // row so the total is never printed twice (mirrors showTotalBlock above).
  const showMoneyBlock = hasRefunds || (isCancelled && netPaidValue === 0);
  const useBoxedNet = isCancelled && netPaidValue === 0;

  const showAmountPaidRow = amount_paid != null && amountPaidValue > 0 && !showTotalBlock && !hasRefunds;

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
  @page { size: 80mm auto; margin: 3mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    width: 72mm;
    font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif;
    font-size: 13px;
    color: #000;
    background: #fff;
  }
  .center { text-align: center; }
  .header-block {
    text-align: center;
    padding: 2mm 0 1.5mm;
    border-bottom: 1px dashed #000;
    margin-bottom: 2mm;
  }
  .clinic-name {
    font-size: 16px;
    font-weight: bold;
  }
  .description {
    font-size: 11px;
    color: #000;
    line-height: 1.25;
    margin-top: 0.5mm;
    white-space: pre-line;
  }
  .token-block {
    text-align: center;
    padding: 1.5mm 0;
    border-bottom: 1px dashed #000;
    margin-bottom: 2mm;
  }
  .token-label { font-size: 11px; letter-spacing: 1px; text-transform: uppercase; color: #000; font-weight: 600; }
  .receipt-no { font-size: 13px; margin-top: 0.5mm; font-weight: bold; }
  .queue-no { font-size: 28px; font-weight: bold; margin: 0.5mm 0; }
  .row { display: flex; justify-content: space-between; padding: 0.5mm 0; font-size: 13px; }
  .row-label { color: #000; }
  .row-value { font-weight: bold; max-width: 48mm; text-align: right; }
  .section-title {
    font-size: 11px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #000;
    font-weight: 600;
    margin-top: 1.5mm;
    padding-top: 1mm;
    border-top: 1px dashed #000;
  }
  .proc-row {
    display: flex;
    justify-content: space-between;
    padding: 0.5mm 0;
    font-size: 12px;
    gap: 2mm;
  }
  .proc-name { flex: 1; min-width: 0; }
  .proc-rx { color: #000; font-size: 10px; }
  .proc-amt { font-weight: bold; white-space: nowrap; }
  .total-block {
    margin-top: 1mm;
    padding-top: 1mm;
    border-top: 1px solid #000;
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    font-weight: bold;
  }
  .footer { text-align: center; margin-top: 1.5mm; font-size: 11px; color: #000; border-top: 1px dashed #000; padding-top: 1mm; }
  .address { font-size: 11px; color: #000; margin-top: 0.5mm; line-height: 1.25; }
  .phone { font-size: 12px; font-weight: bold; color: #000; margin-top: 0.5mm; }
  /* Issue-2 — CANCELLED banner: solid #000 fill + reversed white text (thermal-safe inverse video). */
  .cancel-banner {
    background: #000;
    color: #fff;
    text-align: center;
    padding: 1.5mm 0;
    margin: 2mm 0;
    font-size: 18px;
    font-weight: 800;
    letter-spacing: 1px;
  }
  .cancel-sub { font-size: 11px; font-weight: 600; margin-top: 0.5mm; }
  .net-zero-block {
    margin-top: 1mm;
    padding: 1.5mm;
    border: 2px solid #000;
    display: flex;
    justify-content: space-between;
    font-size: 16px;
    font-weight: 800;
  }
</style>
</head>
<body>
  <div class="header-block">
    <div class="clinic-name">${escapeHtml(clinicLabel.toUpperCase())}</div>
    ${descriptionHtml ? `<div class="description">${descriptionHtml}</div>` : ''}
    ${addressLine ? `<div class="address">${escapeHtml(addressLine)}</div>` : ''}
    ${phone ? `<div class="phone">${escapeHtml(phone)}</div>` : ''}
  </div>

  <div class="token-block">
    ${receiptNumberDisplay ? `<div class="token-label">Receipt No.</div><div class="receipt-no">${escapeHtml(receiptNumberDisplay)}</div>` : ''}
    <div class="token-label" style="margin-top:${receiptNumberDisplay ? '1.5mm' : '0'}">Queue No.</div>
    <div class="queue-no">#${escapeHtml(queue_number || '—')}</div>
    <div style="font-size:11px;color:#000">${escapeHtml(datetimeLabel)}</div>
  </div>

  ${isCancelled ? `
  <div class="cancel-banner">
    CANCELLED
    ${netPaidValue > 0 ? '<div class="cancel-sub">No refund recorded</div>' : ''}
  </div>` : ''}

  <div style="padding: 0 1mm">
    <div class="row">
      <span class="row-label">Patient</span>
      <span class="row-value">${escapeHtml(patientName)}</span>
    </div>
    ${uhid ? `<div class="row"><span class="row-label">UHID</span><span class="row-value">${escapeHtml(uhid)}</span></div>` : ''}
    ${department_name ? `<div class="row"><span class="row-label">Department</span><span class="row-value">${escapeHtml(department_name)}</span></div>` : ''}
    ${advised_by_label ? `<div class="row"><span class="row-label">Advised by</span><span class="row-value">${escapeHtml(advised_by_label)}</span></div>` : `<div class="row">
      <span class="row-label">Doctor</span>
      <span class="row-value">Dr. ${escapeHtml(doctor_name || '—')}</span>
    </div>
    ${advised_by === 'DOCTOR' ? `<div class="row"><span class="row-label">Advised by</span><span class="row-value">Doctor</span></div>` : ''}`}
    ${assignee_name ? `<div class="row"><span class="row-label">${escapeHtml(assignee_label || 'Assigned to')}</span><span class="row-value">${escapeHtml(assignee_name)}</span></div>` : ''}
    ${paymentLabel ? `<div class="row"><span class="row-label">Payment</span><span class="row-value">${escapeHtml(paymentLabel)}</span></div>` : ''}
    ${showAmountPaidRow ? `<div class="row"><span class="row-label">Amount Paid</span><span class="row-value">${formatCurrency(amountPaidValue)}</span></div>` : ''}
  </div>

  ${procedureList.length > 0 ? `
  <div style="padding: 0 1mm">
    <div class="section-title">Procedures</div>
    ${procedureList.map((p) => `
      <div class="proc-row">
        <span class="proc-name">
          ${escapeHtml(p.name || 'Procedure')}
          ${p.receipt_number ? `<span class="proc-rx"> · Rx ${escapeHtml(p.receipt_number)}</span>` : ''}
        </span>
        <span class="proc-amt">${formatCurrency(p.amount)}</span>
      </div>
    `).join('')}
  </div>` : ''}

  ${lineItemsList.length > 0 ? `
  <div style="padding: 0 1mm; margin-top: 1.5mm; padding-top: 1mm; border-top: 1px dashed #000;">
    ${lineItemsList.map((li) => `
      <div class="proc-row">
        <span class="proc-name">${escapeHtml(li.name || '—')}${li.quantity && Number(li.quantity) !== 1 ? ` × ${Number(li.quantity)}` : ''}</span>
        <span class="proc-amt">${formatCurrency(li.amount)}</span>
      </div>
    `).join('')}
  </div>` : ''}

  ${showTotalBlock ? `
  <div style="padding: 0 1mm">
    ${consultationAmount > 0 && procedureList.length > 0 ? `
      <div class="proc-row" style="margin-top:2mm">
        <span class="proc-name" style="color:#000">Consultation</span>
        <span class="proc-amt">${formatCurrency(consultationAmount)}</span>
      </div>` : ''}
    <div class="total-block">
      <span>${lineItemsList.length > 0 ? 'Amount Paid' : 'Total paid today'}</span>
      <span>${formatCurrency(lineItemsList.length > 0 ? lineItemsTotal : totalPaid)}</span>
    </div>
  </div>` : ''}

  ${hasRefunds ? `
  <div style="padding: 0 1mm">
    <div class="section-title">Refunds</div>
    ${refundLinesList.map((rl) => `
      <div class="proc-row">
        <span class="proc-name">
          ${escapeHtml(rl.name || 'Refund')}
          ${rl.receipt_number ? `<span class="proc-rx"> · ${rl.receipt_series === 'CN' ? 'CN' : 'Rx'} ${escapeHtml(rl.receipt_number)}</span>` : ''}
        </span>
        <span class="proc-amt">−${formatCurrency(rl.amount)}</span>
      </div>
    `).join('')}
  </div>` : ''}

  ${showMoneyBlock ? `
  <div style="padding: 0 1mm">
    ${amountPaidValue > 0 ? `<div class="row" style="margin-top:1mm"><span class="row-label">Amount Paid</span><span class="row-value">${formatCurrency(amountPaidValue)}</span></div>` : ''}
    ${hasRefunds ? `<div class="row"><span class="row-label">Refunded</span><span class="row-value">−${formatCurrency(refundedTotal)}</span></div>` : ''}
    <div class="${useBoxedNet ? 'net-zero-block' : 'total-block'}">
      <span>Net Paid</span>
      <span>${formatCurrency(netPaidValue)}</span>
    </div>
  </div>` : ''}

  <div class="footer">Please wait for your turn<br>Thank you for visiting ${escapeHtml(clinicLabel)}</div>
</body>
</html>`;

  printHtml(html);
}

// Shared hidden-iframe print mechanism — write HTML, trigger window.print(),
// then remove the iframe. Factored out of printToken() so a second document
// template (printCreditLabSlip, L2) doesn't duplicate this plumbing.
function printHtml(html) {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;';
  document.body.appendChild(iframe);

  iframe.contentDocument.open();
  iframe.contentDocument.write(html);
  iframe.contentDocument.close();

  let printed = false;

  const doPrint = () => {
    if (printed) return;
    printed = true;
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => {
      if (document.body.contains(iframe)) document.body.removeChild(iframe);
    }, 1000);
  };

  // Wait for content to load before printing
  iframe.onload = () => doPrint();

  // Fallback if onload doesn't fire (e.g. some browsers)
  setTimeout(() => doPrint(), 500);
}

/**
 * printCreditLabSlip — L2 (lab-credit) "LAB ORDER — CREDIT (UNPAID)" thermal
 * slip. A DISTINCT document from the paid OPD lab receipt (printToken above);
 * never shares its template, its "Receipt No." row, or the "Amount Paid" copy
 * — no false payment claim (ruling 7): the slip never carries a receipt
 * number in the paid sense and never claims payment was received.
 *
 * Exact copy is the PO-authored slip in lab-credit/stories.md S2 (Business to
 * ratify) — this renders the SAME sections: org header, "LAB ORDER — CREDIT
 * (UNPAID)" title, patient/UHID/IP-no/ward-bed/age-sex, ordering doctor +
 * dept (the VISIT's department — pass `department_name`, never derive from
 * the viewer), each test qty + amount, the total, a prominent solid-#000
 * "CREDIT — NOT PAID" banner (thermal day-mode-contrast rule), and the
 * "not a payment receipt" footer.
 *
 *   printCreditLabSlip({
 *     clinic_name, clinic_description, address, pincode, phone, gstin,
 *     patient: { first_name, last_name, patient_id, uhid, age, gender },
 *     ip_number, ward_bed_label,           // '—' when absent/not admitted
 *     doctor_name, department_name,
 *     visit_date,                          // → date/time line (falls back to now)
 *     tests: [{ name, qty, amount }, ...],
 *   })
 *
 * T-UHID-BUG (owner call, 2026-08-26) — this slip carries an amount so it's
 * treated as a MONEY document: full real UHID (`patient.uhid`) when minted,
 * full `patient.patient_id` fallback otherwise — never the last-5 fragment
 * `deriveUhid` used to render here (same fix as the paid OPD token above).
 */
export function printCreditLabSlip({
  clinic_name,
  clinic_description,
  address,
  pincode,
  phone,
  gstin,
  patient,
  ip_number,
  ward_bed_label,
  doctor_name,
  department_name,
  visit_date,
  tests,
}) {
  const patientName = patient
    ? `${patient.first_name || ''} ${patient.last_name || ''}`.trim()
    : '—';
  const uhid = resolveUhid({ uhid: patient?.uhid, patient_id: patient?.patient_id });
  const genderLabel = patient?.gender === 'MALE' ? 'M'
    : patient?.gender === 'FEMALE' ? 'F'
      : patient?.gender === 'OTHER' ? 'O'
        : (patient?.gender || '');
  const ageSex = [patient?.age != null ? String(patient.age) : '', genderLabel].filter(Boolean).join('/') || '—';
  const clinicLabel = clinic_name || 'Clinic';
  const dateLabel = formatDate(visit_date);
  const timeLabel = formatTime(timePartOf(visit_date)) || formatTime(new Date());
  const addressLine = [address, pincode].filter(Boolean).join(' ').trim();
  const testList = Array.isArray(tests) ? tests : [];
  const total = testList.reduce((sum, t) => sum + Number(t.amount || 0), 0);

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
  @page { size: 80mm auto; margin: 3mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { width: 72mm; font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 13px; color: #000; background: #fff; }
  .center { text-align: center; }
  .header-block { text-align: center; padding: 2mm 0 1.5mm; border-bottom: 1px dashed #000; margin-bottom: 2mm; }
  .clinic-name { font-size: 16px; font-weight: bold; }
  .description { font-size: 11px; color: #000; line-height: 1.25; margin-top: 0.5mm; white-space: pre-line; }
  .address { font-size: 11px; color: #000; margin-top: 0.5mm; line-height: 1.25; }
  .phone { font-size: 12px; font-weight: bold; color: #000; margin-top: 0.5mm; }
  .title-block { text-align: center; padding: 1.5mm 0; border-top: 1px dashed #000; border-bottom: 1px dashed #000; margin-bottom: 2mm; font-size: 13px; font-weight: 800; letter-spacing: 0.5px; }
  .row { display: flex; justify-content: space-between; padding: 0.5mm 0; font-size: 13px; gap: 2mm; }
  .row-label { color: #000; }
  .row-value { font-weight: bold; max-width: 48mm; text-align: right; }
  .proc-row { display: flex; justify-content: space-between; padding: 0.5mm 0; font-size: 12px; gap: 2mm; }
  .proc-name { flex: 1; min-width: 0; }
  .proc-amt { font-weight: bold; white-space: nowrap; }
  .total-block { margin-top: 1mm; padding-top: 1mm; border-top: 1px solid #000; display: flex; justify-content: space-between; font-size: 14px; font-weight: bold; }
  /* Prominent solid-#000 CREDIT banner — same inverse-video pattern as the
     existing CANCELLED banner (thermal day-mode-contrast rule: solid fill +
     big bold text, never a faint tint). */
  .credit-banner { background: #000; color: #fff; text-align: center; padding: 2mm 0; margin: 2mm 0; font-size: 18px; font-weight: 800; letter-spacing: 1px; }
  .credit-sub { font-size: 11px; font-weight: 600; margin-top: 0.75mm; }
  .footer { text-align: center; margin-top: 2mm; font-size: 11px; color: #000; border-top: 1px dashed #000; padding-top: 1.5mm; line-height: 1.4; }
</style>
</head>
<body>
  <div class="header-block">
    <div class="clinic-name">${escapeHtml(clinicLabel.toUpperCase())}</div>
    ${clinic_description ? `<div class="description">${escapeHtml(clinic_description).replace(/\r?\n/g, '<br>')}</div>` : ''}
    ${addressLine ? `<div class="address">${escapeHtml(addressLine)}${gstin ? ` · GSTIN ${escapeHtml(gstin)}` : ''}</div>` : (gstin ? `<div class="address">GSTIN ${escapeHtml(gstin)}</div>` : '')}
    ${phone ? `<div class="phone">${escapeHtml(phone)}</div>` : ''}
  </div>

  <div class="title-block">LAB ORDER — CREDIT (UNPAID)</div>

  <div style="padding: 0 1mm">
    <div class="row"><span class="row-label">Patient</span><span class="row-value">${escapeHtml(patientName)}</span></div>
    <div class="row"><span class="row-label">UHID</span><span class="row-value">${escapeHtml(uhid || '—')}</span></div>
    <div class="row"><span class="row-label">IP No.</span><span class="row-value">${escapeHtml(ip_number || '—')}</span></div>
    <div class="row"><span class="row-label">Ward/Bed</span><span class="row-value">${escapeHtml(ward_bed_label || '—')}</span></div>
    <div class="row"><span class="row-label">Age/Sex</span><span class="row-value">${escapeHtml(ageSex)}</span></div>
    <div class="row"><span class="row-label">Ordered by</span><span class="row-value">Dr. ${escapeHtml(doctor_name || '—')}${department_name ? ` · ${escapeHtml(department_name)}` : ''}</span></div>
    <div class="row"><span class="row-label">Date</span><span class="row-value">${escapeHtml(dateLabel)} ${escapeHtml(timeLabel)}</span></div>
  </div>

  <div style="padding: 0 1mm; margin-top: 1.5mm; padding-top: 1mm; border-top: 1px dashed #000;">
    ${testList.map((t) => `
      <div class="proc-row">
        <span class="proc-name">${escapeHtml(t.name || '—')}${t.qty && Number(t.qty) !== 1 ? ` × ${Number(t.qty)}` : ''}</span>
        <span class="proc-amt">${formatCurrency(t.amount)}</span>
      </div>
    `).join('')}
    <div class="total-block">
      <span>Total (on credit)</span>
      <span>${formatCurrency(total)}</span>
    </div>
  </div>

  <div class="credit-banner">
    CREDIT — NOT PAID
    <div class="credit-sub">Payment to be settled at the billing desk.</div>
  </div>

  <div class="footer">This is a lab order slip, not a payment receipt.<br>No payment has been collected against this order.</div>
</body>
</html>`;

  printHtml(html);
}

// The credit slip's `visit_date` carries only a date ("YYYY-MM-DD") — there's
// no separate visit_time field (unlike the OPD token). formatTime() needs an
// actual time-bearing value to extract HH:MM from, so pass the raw
// `visit_date` through only when it looks like a full ISO datetime;
// otherwise the caller falls back to "now" (`formatTime(new Date())`).
function timePartOf(visitDate) {
  return typeof visitDate === 'string' && visitDate.length > 10 ? visitDate : null;
}
